import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import {
  Skeleton,
  Surface,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
  srOnly as billingSrOnly,
} from '@kilat/ui';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { tenantsRepo } from '../lib/repositories/tenants-repo.js';
import {
  tenantPayoutBankRepoFor,
  TENANT_PAYOUT_BANK_DOC_ID,
} from '../lib/repositories/tenant-payout-bank-repo.js';
import { subscriptionRepoFor } from '../lib/repositories/subscription-repo.js';
import { subscriptionEventsRepoFor } from '../lib/repositories/subscription-events-repo.js';
import {
  tenantTaxRepoFor,
  TENANT_TAX_DOC_ID,
  type TenantTaxDoc,
} from '../lib/repositories/tenant-tax-repo.js';
import { outletDocRepoFor } from '../lib/repositories/outlet-repos.js';
import { invoicesRepoFor } from '../lib/repositories/invoices-repo.js';
import { tenantWalletLedgerSnapshot } from '../lib/repositories/tenant-wallet-repo.js';
import {
  callRunMonthlyBillingNow,
  callRetryToyyibPayBill,
  callGenerateInvoicePdf,
  callSetTenantTaxConfig,
  callSetPayoutBankAccount,
} from '../lib/callables/billing.js';
import { callRequestTenantWithdraw } from '../lib/callables/payouts.js';
import { logger } from '../lib/logger.js';
import { computeTrialMsLeft } from '../lib/trial-countdown.js';
import { defaultPlanId, defaultPlans } from '@kilat/config-seed';
import { Timestamp } from 'firebase/firestore';
import {
  computeTenantWalletSummary,
  money,
  phone as phoneUtil,
  proration,
  type Invoice,
  type SubscriptionEvent,
  type SubscriptionState,
  type TenantPayoutBankAccount,
  type TenantProfile,
  type TenantWalletLedgerEntry,
} from '@kilat/shared';

/**
 * /billing — operator subscription overview.
 *
 * Payment rail: toyyibPay FPX (ADR-0022 Option B) — invoices carry a
 * `paymentLink`, and the Pay-now CTA + retryToyyibPayBill callable manage it.
 * This page surfaces the full picture so the operator can answer at a glance:
 *
 *   - "What's my current plan / trial state / next billing date?"
 *   - "How many outlets am I being charged for right now?"
 *   - "What changes are pending that will hit my next bill?"
 *   - "What was charged when, and why?"
 *
 * Everything live-subscribed: as the operator adds/closes outlets in /outlets,
 * this page updates in real time. The audit log shows the full
 * `subscriptionEvents` ledger so there are no mystery charges.
 *
 * Premium-logical: every state explicit (loading / signed-out / no-tenant /
 * no-subscription-doc / live / save-error). Numbers tabular-aligned; every
 * pending change names its effective date so the operator knows what's about
 * to happen.
 */
export function BillingPage() {
  const { user, claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const { t } = useI18n();

  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const isSuperAdmin =
    claims !== null &&
    typeof (claims as { superadmin?: Record<string, unknown> }).superadmin === 'object' &&
    (claims as { superadmin: Record<string, unknown> }).superadmin[brand.id] === true;
  // Only the OWNER may change the payout destination (setPayoutBankAccount).
  // A manager sees it read-only: a manager can request a withdrawal, and one
  // who could also repoint the account could pay the money to themselves.
  const rolesClaim = claims?.['roles'];
  const isOwner =
    tenantId !== null &&
    typeof rolesClaim === 'object' &&
    rolesClaim !== null &&
    (rolesClaim as Record<string, unknown>)[tenantId] === 'owner';

  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [payoutBankAccount, setPayoutBankAccount] = useState<TenantPayoutBankAccount | null>(null);
  const [payoutBankLoadFailed, setPayoutBankLoadFailed] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileReloadTick, setProfileReloadTick] = useState(0);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const doc = await tenantsRepo.get(tenantId);
        if (cancelled) return;
        setProfile(doc?.profile ?? null);
        // The payout destination is a PRIVATE document (owner/manager read),
        // not a field on the world-readable tenant root. Its own failure must
        // not blank the whole billing page, so it is caught separately.
        try {
          const bank = await tenantPayoutBankRepoFor(tenantId).get(TENANT_PAYOUT_BANK_DOC_ID);
          if (cancelled) return;
          setPayoutBankAccount(bank ?? null);
          setPayoutBankLoadFailed(false);
        } catch (e) {
          logger.error('billing: payout destination load failed', { error: String(e) });
          if (!cancelled) setPayoutBankLoadFailed(true);
        }
        setProfileLoaded(true);
      } catch (e) {
        logger.error('billing: tenant load failed', { error: String(e) });
        if (!cancelled) {
          setProfileError(String((e as Error).message ?? 'Could not load tenant.'));
          setProfileLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, profileReloadTick]);

  const subSubscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = subscriptionRepoFor(tenantId);
    return (cb: (v: SubscriptionState | null) => void) => repo.snapshot('current', cb);
  }, [tenantId]);
  const { data: subscription, loading: subLoading } = useTrackedSnapshot<SubscriptionState | null>(
    ['subscription', tenantId],
    subSubscribe,
  );

  const eventsSubscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = subscriptionEventsRepoFor(tenantId);
    return (cb: (vs: SubscriptionEvent[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const { data: events } = useTrackedSnapshot<SubscriptionEvent[]>(
    ['subscriptionEvents', tenantId],
    eventsSubscribe,
  );

  const outletsSubscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = outletDocRepoFor(tenantId);
    return (cb: (vs: unknown[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const { data: outletDocs } = useTrackedSnapshot<unknown[]>(
    ['outlets-for-billing', tenantId],
    outletsSubscribe,
  );

  const invoicesSubscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = invoicesRepoFor(tenantId);
    return (cb: (vs: Invoice[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const { data: invoices } = useTrackedSnapshot<Invoice[]>(
    ['invoices', tenantId],
    invoicesSubscribe,
  );

  // Every gate state below reaches a real operator, and they were raw English
  // until 2026-08-25 — a Malay or Tamil operator whose claims were still syncing
  // read "No tenant context yet" on their own billing screen.
  const backToQueue = { label: t('dashboard.billing.gate.backToQueue'), to: '/queue' };

  if (authLoading)
    return (
      <Screen
        title={t('dashboard.billing.gate.signingIn')}
        body={t('dashboard.billing.gate.signingInBody')}
      />
    );
  if (!user) {
    return (
      <Screen
        title={t('dashboard.billing.gate.signedOut')}
        body={t('dashboard.billing.gate.signedOutBody')}
        cta={backToQueue}
      />
    );
  }
  if (!tenantId) {
    return (
      <Screen
        title={t('dashboard.billing.gate.noTenant')}
        body={t('dashboard.billing.gate.noTenantBody')}
        cta={backToQueue}
      />
    );
  }
  if (!profileLoaded || subLoading) {
    return <BillingSkeleton label={t('common.loading')} />;
  }
  if (profileError) {
    return (
      <Screen
        title={t('dashboard.billing.gate.loadFailed')}
        body={profileError}
        cta={backToQueue}
      />
    );
  }
  if (!profile) {
    return (
      <Screen
        title={t('dashboard.billing.gate.tenantMissing')}
        body={t('dashboard.billing.gate.tenantMissingBody')}
        cta={backToQueue}
      />
    );
  }
  if (!subscription) {
    return (
      <Screen
        title={t('dashboard.billing.gate.noSubscription')}
        body={t('dashboard.billing.gate.noSubscriptionBody')}
        cta={backToQueue}
      />
    );
  }

  const plan =
    defaultPlans.find((p) => p.id === profile.planId) ??
    defaultPlans.find((p) => p.id === defaultPlanId);

  const outlets = (outletDocs ?? []) as {
    id?: string;
    profile?: { name?: string; status?: string };
  }[];
  const activeOutlets = outlets.filter((o) => o.profile?.status === 'active');
  const softClosedOutlets = outlets.filter((o) => o.profile?.status === 'soft_closed');

  const sortedEvents = (events ?? []).slice().sort((a, b) => tsMs(b.at) - tsMs(a.at));
  const pendingRemovals = buildPendingRemovals(events ?? []);

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              letterSpacing: 4,
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            {t('dashboard.billing.header.eyebrow')}
          </p>
          <h1
            style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 'var(--space-2) 0 0 0' }}
          >
            {t('dashboard.billing.header.title')}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          {isSuperAdmin ? <RunMonthlyBillingButton /> : null}
          <Link to="/queue" style={ghostLinkStyle}>
            {t('dashboard.billing.header.backToQueue')}
          </Link>
        </div>
      </header>

      <div className="layout-split-aside">
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <PlanCard
            plan={plan}
            subscription={subscription}
            activeOutletCount={activeOutlets.length}
            softClosedCount={softClosedOutlets.length}
          />

          <ZeroCutNote />

          <ProfileCard tenantId={tenantId} profile={profile} />

          <PendingChangesCard
            pendingRemovals={pendingRemovals}
            outletsById={Object.fromEntries(
              outlets.map((o) => [o.id ?? '', o.profile?.name ?? o.id ?? '—']),
            )}
          />

          <WalletCard
            tenantId={tenantId}
            payoutBankAccount={payoutBankAccount}
            payoutBankLoadFailed={payoutBankLoadFailed}
            canEditPayoutBank={isOwner}
            onPayoutBankAccountSaved={() => setProfileReloadTick((n) => n + 1)}
          />

          {profile ? <TaxConfigCard tenantId={tenantId} /> : null}

          <InvoicesCard tenantId={tenantId} invoices={invoices ?? []} />

          <AuditLogCard events={sortedEvents} />
        </div>

        <Surface as="aside">
          <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {t('dashboard.billing.forecast.title')}
          </h2>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t('dashboard.billing.forecast.subtitle')}
          </p>
          {plan ? (
            <NextMonthForecast
              plan={plan}
              activeOutletCount={activeOutlets.length}
              pendingRemovalCount={pendingRemovals.length}
              now={new Date()}
            />
          ) : (
            <p style={{ margin: 'var(--space-3) 0 0 0', color: 'var(--color-accent)' }}>
              {t('dashboard.billing.forecast.planMissing')}
            </p>
          )}
          <p
            style={{
              margin: 'var(--space-4) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            <strong>{t('dashboard.billing.forecast.noteLead')}</strong>{' '}
            {t('dashboard.billing.forecast.noteBody')}
          </p>
        </Surface>
      </div>
    </main>
  );
}

// ── plan card ─────────────────────────────────────────────────────────

/**
 * Restate the zero-cut promise where the operator is looking at money (G21).
 *
 * It was made once at signup and never again. That is the wrong shape for a
 * promise whose whole point is trust: the moment an operator is reading an
 * invoice is exactly when they wonder what the platform is taking, and silence
 * there invites the assumption that something is.
 *
 * Deliberately plain and unmissable rather than a footnote. The claim is
 * unusual enough in this market that burying it wastes it.
 */
function ZeroCutNote() {
  const { t } = useI18n();
  return (
    <Surface accent="success" as="aside">
      <p style={{ margin: 0, fontWeight: 600 }}>{t('dashboard.billing.zeroCut.title')}</p>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          color: 'var(--color-on-surface-muted)',
          fontSize: 'var(--text-meta)',
          lineHeight: 1.5,
        }}
      >
        {t('dashboard.billing.zeroCut.body')}
      </p>
    </Surface>
  );
}

function PlanCard({
  plan,
  subscription,
  activeOutletCount,
  softClosedCount,
}: {
  readonly plan: ReturnType<typeof defaultPlans.find>;
  readonly subscription: SubscriptionState;
  readonly activeOutletCount: number;
  readonly softClosedCount: number;
}) {
  const { t } = useI18n();
  if (!plan) return null;
  const trialMsLeft = computeTrialMsLeft(subscription, Date.now());
  const trialDays =
    trialMsLeft === null ? 0 : Math.max(0, Math.round(trialMsLeft / (24 * 60 * 60 * 1000)));
  const trialDaysLabel =
    trialDays === 1
      ? t('dashboard.billing.plan.trialDaysOne')
      : t('dashboard.billing.plan.trialDaysOther', { n: trialDays });
  const includedOutletsUsed = Math.min(activeOutletCount, plan.includedOutlets);
  const extraOutletCount = Math.max(0, activeOutletCount - plan.includedOutlets);
  const monthlyTotal = plan.basePrice + extraOutletCount * plan.extraOutletPrice;
  return (
    <Surface as="section">
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t('dashboard.billing.plan.eyebrow')}
          </p>
          <h2
            style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h2)', fontWeight: 600 }}
          >
            {plan.name}
          </h2>
        </div>
        <StatusBadge status={subscription.status} />
      </header>

      {trialMsLeft !== null ? (
        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {subscription.trialEndsAt
            ? t('dashboard.billing.plan.trialEndsInWithDate', {
                days: trialDaysLabel,
                date: formatDate(tsMs(subscription.trialEndsAt)),
              })
            : t('dashboard.billing.plan.trialEndsIn', { days: trialDaysLabel })}
        </p>
      ) : null}

      <dl style={statGrid}>
        <Stat
          label={t('dashboard.billing.stat.basePrice')}
          value={`${money.format(plan.basePrice)}/mo`}
          hint={`covers ${plan.includedOutlets} outlet`}
        />
        <Stat
          label={t('dashboard.billing.stat.extraOutlets')}
          value={
            extraOutletCount === 0
              ? '—'
              : `${extraOutletCount} × ${money.format(plan.extraOutletPrice)}/mo`
          }
        />
        <Stat
          label={t('dashboard.billing.stat.outletsActive')}
          value={String(activeOutletCount)}
          hint={`${includedOutletsUsed} included · ${extraOutletCount} extra`}
        />
        {softClosedCount > 0 ? (
          <Stat
            label={t('dashboard.billing.stat.closingThisPeriod')}
            value={String(softClosedCount)}
            hint={t('dashboard.billing.stat.closingHint')}
          />
        ) : null}
        <Stat
          label={t('dashboard.billing.stat.nextBillingDate')}
          value={formatDate(tsMs(subscription.nextBillingDate))}
        />
        <Stat
          label={t('dashboard.billing.stat.paymentMethod')}
          value={prettyPaymentMethod(subscription.paymentMethod)}
        />
      </dl>

      <hr
        style={{
          border: 'none',
          borderTop: '1px solid var(--color-border)',
          margin: 'var(--space-4) 0 var(--space-3) 0',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {t('dashboard.billing.plan.fullMonthTotal')}
        </span>
        <strong style={{ fontVariantNumeric: 'tabular-nums', fontSize: 'var(--text-h2)' }}>
          {money.format(monthlyTotal)}
        </strong>
      </div>
    </Surface>
  );
}

// ── pending changes card ──────────────────────────────────────────────

function PendingChangesCard({
  pendingRemovals,
  outletsById,
}: {
  readonly pendingRemovals: { scheduledEventId: string; outletId: string; effectiveAtMs: number }[];
  readonly outletsById: Record<string, string>;
}) {
  const { t } = useI18n();
  return (
    <Surface as="section">
      <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
        {t('dashboard.billing.pending.title')}
      </h2>
      <p
        style={{
          margin: 'var(--space-1) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t('dashboard.billing.pending.subtitle')}
      </p>

      {pendingRemovals.length === 0 ? (
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            padding: 'var(--space-3) var(--space-4)',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            background: 'var(--tint-subtle)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {t('dashboard.billing.pending.empty')}
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 'var(--space-3) 0 0 0',
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {pendingRemovals.map((rem) => (
            <li
              key={rem.scheduledEventId}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 'var(--space-3)',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(245,158,11,0.06)',
                border: '1px solid rgba(245,158,11,0.22)',
              }}
            >
              <div>
                <p style={{ margin: 0, fontWeight: 500 }}>
                  {t('dashboard.billing.pending.outletCloses', {
                    outlet: outletsById[rem.outletId] ?? rem.outletId,
                  })}
                </p>
                <p
                  style={{
                    margin: 'var(--space-1) 0 0 0',
                    fontSize: 'var(--text-meta)',
                    color: 'var(--color-on-surface-muted)',
                  }}
                >
                  {t('dashboard.billing.pending.effective', {
                    date: formatDate(rem.effectiveAtMs),
                  })}
                </p>
              </div>
              <Link
                to="/outlets"
                style={miniGhostButtonStyle}
                title={t('dashboard.billing.action.manageOutletsTitle')}
              >
                {t('dashboard.billing.pending.manage')}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}

// ── profile card (Sprint 45) ──────────────────────────────────

/**
 * Inline editor for `tenant.profile.{businessName, contact.email, contact.phone}`.
 *
 * Edit-in-place pattern:
 *  - Display state shows current values; "Edit" button reveals the form.
 *  - Each field is dirty-tracked; "Save changes" only enabled if something
 *    actually changed AND all fields validate.
 *  - Phone validates through the shared `phoneUtil.normaliseMalaysianPhone`
 *    and surfaces a live three-tone hint (green / amber / muted).
 *  - Cancel resets to the persisted state and collapses the form.
 *  - Save writes via `tenantsRepo.update` using dotted-path keys (the
 *    Firestore SDK already supports this; security rules permit `owner` /
 *    `manager` of the tenant).
 *  - Success: brief "Saved." flash + collapse. Error: stays open with a
 *    red note explaining what went wrong (per [feedback-no-dead-ends]).
 *  - The `syncBackrefStatus` trigger (Sprint 45 expanded) propagates the
 *    changes to the agent back-ref within seconds, so /operators in the
 *    agent PWA stays current without a separate write here.
 */
function ProfileCard({
  tenantId,
  profile,
}: {
  readonly tenantId: string;
  readonly profile: TenantProfile;
}) {
  const { t } = useI18n();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [businessName, setBusinessName] = useState(profile.businessName);
  const [email, setEmail] = useState(profile.contact.email);
  const [phone, setPhone] = useState(profile.contact.phone);
  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // Reset local fields whenever the upstream profile changes (e.g., a
  // concurrent save from another tab, or super-admin edit) — but only
  // when we're in view mode so we never clobber the user's in-progress
  // edit. (The edit form will surface as a stale-data warning if the
  // server-side value differs at save-time; covered by the catch below.)
  useEffect(() => {
    if (mode !== 'view') return;
    setBusinessName(profile.businessName);
    setEmail(profile.contact.email);
    setPhone(profile.contact.phone);
  }, [mode, profile.businessName, profile.contact.email, profile.contact.phone]);

  const normalisedPhone = useMemo<string | null>(() => {
    if (!phone.trim()) return null;
    return phoneUtil.normaliseMalaysianPhone(phone);
  }, [phone]);

  const phoneTone: 'muted' | 'success' | 'warn' =
    phone.trim().length === 0
      ? 'warn' // empty is now an error post-Sprint-43; phone is required
      : normalisedPhone
        ? 'success'
        : 'warn';
  const phoneHint =
    phone.trim().length === 0
      ? 'Required. Used for invoice receipts and account messages.'
      : normalisedPhone
        ? `Looks good: ${phoneUtil.formatE164(normalisedPhone)}`
        : "Doesn't look like a Malaysia mobile. Try +60123456789 or 0123456789.";

  const trimmedName = businessName.trim();
  const trimmedEmail = email.trim();
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

  const dirty =
    trimmedName !== profile.businessName ||
    trimmedEmail !== profile.contact.email ||
    (normalisedPhone ?? phone) !== profile.contact.phone;

  const canSave =
    dirty &&
    trimmedName.length > 0 &&
    emailLooksValid &&
    normalisedPhone !== null &&
    stage.kind !== 'saving';

  function reset() {
    setBusinessName(profile.businessName);
    setEmail(profile.contact.email);
    setPhone(profile.contact.phone);
    setStage({ kind: 'idle' });
    setMode('view');
  }

  async function save() {
    if (!canSave || normalisedPhone === null) return;
    setStage({ kind: 'saving' });
    try {
      await tenantsRepo.update(tenantId, {
        'profile.businessName': trimmedName,
        'profile.contact.email': trimmedEmail,
        'profile.contact.phone': normalisedPhone,
        'profile.updatedAt': Timestamp.now(),
      });
      setStage({ kind: 'saved' });
      logger.info('profile updated', {
        tenantId,
        fields: ['businessName', 'email', 'phone'].filter((f) =>
          f === 'businessName'
            ? trimmedName !== profile.businessName
            : f === 'email'
              ? trimmedEmail !== profile.contact.email
              : normalisedPhone !== profile.contact.phone,
        ),
      });
      setTimeout(() => {
        setStage({ kind: 'idle' });
        setMode('view');
      }, 1200);
    } catch (e) {
      const msg = (e as Error).message ?? 'Unknown error';
      setStage({ kind: 'error', message: msg });
      logger.error('profile update failed', { tenantId, error: msg });
    }
  }

  if (mode === 'view') {
    return (
      <Surface as="section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {t('dashboard.billing.profile.title')}
          </h2>
          <button
            type="button"
            onClick={() => setMode('edit')}
            style={{
              background: 'transparent',
              border: '1px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-pill)',
              padding: '6px 12px',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface)',
              cursor: 'pointer',
            }}
          >
            {t('dashboard.billing.profile.edit')}
          </button>
        </div>
        <p
          style={{
            margin: 'var(--space-1) 0 var(--space-3) 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t('dashboard.billing.profile.viewSubtitle')}
        </p>
        <dl
          style={{
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            columnGap: 'var(--space-4)',
            rowGap: 'var(--space-2)',
          }}
        >
          <dt style={profileDt}>{t('dashboard.billing.profile.businessName')}</dt>
          <dd style={profileDd}>{profile.businessName}</dd>
          <dt style={profileDt}>{t('dashboard.billing.profile.contactEmail')}</dt>
          <dd style={profileDd}>{profile.contact.email}</dd>
          <dt style={profileDt}>{t('dashboard.billing.profile.contactPhone')}</dt>
          <dd style={profileDd}>
            {profile.contact.phone ? (
              phoneUtil.formatE164(profile.contact.phone)
            ) : (
              <span style={{ color: '#b45309' }}>
                {t('dashboard.billing.profile.phoneMissing')}
              </span>
            )}
          </dd>
        </dl>
      </Surface>
    );
  }

  return (
    <Surface as="section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t('dashboard.billing.profile.editTitle')}
        </h2>
      </div>
      <p
        style={{
          margin: 'var(--space-1) 0 var(--space-3) 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t('dashboard.billing.profile.editSubtitle')}
      </p>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <ProfileField
          label={t('dashboard.billing.profileField.businessName')}
          value={businessName}
          onChange={setBusinessName}
          autoComplete="organization"
          required
        />
        <ProfileField
          label={t('dashboard.billing.profileField.contactEmail')}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
          hint={
            emailLooksValid || trimmedEmail.length === 0
              ? undefined
              : t('dashboard.billing.validation.emailInvalid')
          }
          hintTone="warn"
        />
        <ProfileField
          label={t('dashboard.billing.profileField.contactPhone')}
          type="tel"
          value={phone}
          onChange={setPhone}
          autoComplete="tel"
          required
          hint={phoneHint}
          hintTone={phoneTone}
        />
        <div
          style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}
        >
          <button
            type="button"
            onClick={save}
            disabled={!canSave}
            style={{
              background: canSave ? 'var(--color-primary)' : 'var(--color-border-strong)',
              color: canSave ? 'var(--color-primary-fg)' : 'var(--color-on-surface-muted)',
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              padding: '8px 16px',
              fontWeight: 500,
              fontSize: 'var(--text-meta)',
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {stage.kind === 'saving'
              ? t('dashboard.billing.action.saving')
              : t('dashboard.billing.action.saveChanges')}
          </button>
          <button
            type="button"
            onClick={reset}
            style={{
              background: 'transparent',
              border: '1px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-pill)',
              padding: '8px 16px',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface)',
              cursor: 'pointer',
            }}
          >
            {t('dashboard.billing.profile.cancel')}
          </button>
          {stage.kind === 'saved' ? (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-primary)' }}>
              {t('dashboard.billing.profile.saved')}
            </span>
          ) : null}
          {stage.kind === 'error' ? (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}>
              {stage.message}
            </span>
          ) : null}
          {dirty ? null : (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {t('dashboard.billing.profile.noChanges')}
            </span>
          )}
        </div>
      </div>
    </Surface>
  );
}

const profileDt: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};
const profileDd: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  color: 'var(--color-on-surface)',
};

interface ProfileFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly type?: string;
  readonly required?: boolean;
  readonly autoComplete?: string;
  readonly hint?: string;
  readonly hintTone?: 'muted' | 'success' | 'warn';
}

function ProfileField({
  label,
  value,
  onChange,
  type = 'text',
  required,
  autoComplete,
  hint,
  hintTone = 'muted',
}: ProfileFieldProps) {
  const hintColour =
    hintTone === 'success'
      ? 'var(--color-primary)'
      : hintTone === 'warn'
        ? '#b45309'
        : 'var(--color-on-surface-muted)';
  return (
    <label style={{ display: 'grid', gap: 'var(--space-1)' }}>
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required ?? false}
        autoComplete={autoComplete ?? 'off'}
        style={{
          padding: '10px 12px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--color-border-strong)',
          fontSize: 'var(--text-body)',
          fontFamily: 'inherit',
          background: 'var(--color-surface)',
          color: 'var(--color-on-surface)',
        }}
      />
      {hint ? (
        <span
          style={{ fontSize: 'var(--text-meta)', color: hintColour, lineHeight: 1.4 }}
          aria-live="polite"
        >
          {hint}
        </span>
      ) : null}
    </label>
  );
}

// ── invoices card (Sprint 26) ─────────────────────────────────────────

function InvoicesCard({
  tenantId,
  invoices,
}: {
  readonly tenantId: string;
  readonly invoices: readonly Invoice[];
}) {
  const { t } = useI18n();
  const sorted = invoices.slice().sort((a, b) => tsMs(b.periodStart) - tsMs(a.periodStart));
  return (
    <Surface as="section">
      <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
        {t('dashboard.billing.invoices.title')}
      </h2>
      <p
        style={{
          margin: 'var(--space-1) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t('dashboard.billing.invoices.generatedNote')}
      </p>
      {sorted.length === 0 ? (
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            padding: 'var(--space-3) var(--space-4)',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            background: 'var(--tint-subtle)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {t('dashboard.billing.invoices.empty')}
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 'var(--space-3) 0 0 0',
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {sorted.map((inv) => (
            <li key={inv.id}>
              <details
                style={{
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-surface)',
                  padding: 'var(--space-3) var(--space-4)',
                }}
              >
                <summary
                  style={{
                    listStyle: 'none',
                    cursor: 'pointer',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto',
                    gap: 'var(--space-3)',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <p style={{ margin: 0, fontWeight: 500 }}>{inv.id}</p>
                    <p
                      style={{
                        margin: 'var(--space-1) 0 0 0',
                        fontSize: 'var(--text-meta)',
                        color: 'var(--color-on-surface-muted)',
                      }}
                    >
                      {t('dashboard.billing.invoices.period', {
                        start: formatDate(tsMs(inv.periodStart)),
                        end: formatDate(tsMs(inv.periodEnd)),
                        due: formatDate(tsMs(inv.dueDate)),
                      })}
                    </p>
                  </div>
                  <InvoiceStatusPill status={inv.status} />
                  <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {money.format(inv.total)}
                  </span>
                </summary>
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 'var(--space-3) 0 0 0',
                    padding: 'var(--space-3) 0 0 0',
                    borderTop: '1px solid var(--color-border)',
                    display: 'grid',
                    gap: 'var(--space-2)',
                  }}
                >
                  {inv.lineItems
                    .filter((li) => li.kind !== 'sst')
                    .map((li, i) => (
                      <li
                        key={i}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          fontSize: 'var(--text-meta)',
                        }}
                      >
                        <span style={{ color: 'var(--color-on-surface-muted)' }}>
                          {li.description}
                        </span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {money.format(li.amount)}
                        </span>
                      </li>
                    ))}
                  {/* Sprint 91 — three-line totals block surfaces the
                      tax breakdown clearly when SST applies. */}
                  {inv.tax > 0 ? (
                    <>
                      <li
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          paddingTop: 'var(--space-2)',
                          borderTop: '1px solid var(--color-border)',
                          fontSize: 'var(--text-meta)',
                          color: 'var(--color-on-surface-muted)',
                        }}
                      >
                        <span>{t('dashboard.billing.invoices.subtotal')}</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {money.format(inv.subtotal)}
                        </span>
                      </li>
                      <li
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          fontSize: 'var(--text-meta)',
                          color: 'var(--color-on-surface-muted)',
                        }}
                      >
                        <span>SST</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {money.format(inv.tax)}
                        </span>
                      </li>
                    </>
                  ) : null}
                  <li
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      paddingTop: 'var(--space-2)',
                      borderTop: '1px solid var(--color-border)',
                      fontWeight: 600,
                    }}
                  >
                    <span>{t('dashboard.billing.invoices.total')}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {money.format(inv.total)}
                    </span>
                  </li>
                </ul>
                <PaymentLinkCta tenantId={tenantId} invoice={inv} />
                <div
                  style={{
                    marginTop: 'var(--space-2)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <InvoicePdfButton tenantId={tenantId} invoiceId={inv.id} />
                </div>
              </details>
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}

/**
 * Per-invoice payment-link affordance.
 *
 *  - `paid`             → quiet "Settled on {paidAt}" — no CTA
 *  - `void` / `refunded`→ quiet "Invoice closed" — no CTA
 *  - `open` + has link  → Pay-now (opens link) + Copy + Regenerate
 *  - `open` + no link   → "Generate payment link" CTA
 *
 * The retry callable returns a structured outcome on routine failures
 * (already_paid / invoice_closed / amount_invalid / email_missing /
 * secrets_missing / gateway_failed); the UI surfaces each with a
 * tailored next-action so the operator is never stranded
 * (per [feedback-no-dead-ends]).
 */
function PaymentLinkCta({
  tenantId,
  invoice,
}: {
  readonly tenantId: string;
  readonly invoice: Invoice;
}) {
  const [stage, setStage] = useState<
    | { kind: 'idle' }
    | { kind: 'busy' }
    | { kind: 'ok'; paymentUrl: string }
    | { kind: 'failed'; message: string }
    | { kind: 'copied' }
  >({ kind: 'idle' });
  const { t } = useI18n();

  // Closed-state branches — no CTA, just status copy.
  if (invoice.status === 'paid') {
    const paidMs = invoice.paidAt ? tsMs(invoice.paidAt) : 0;
    return (
      <div style={paymentNoteStyle}>
        {paidMs
          ? t('dashboard.billing.paymentNote.settledOn', { date: formatDate(paidMs) })
          : t('dashboard.billing.paymentNote.settled')}
      </div>
    );
  }
  if (
    invoice.status === 'void' ||
    invoice.status === 'refunded' ||
    invoice.status === 'partially_refunded'
  ) {
    return (
      <div style={paymentNoteStyle}>
        {invoice.status === 'void'
          ? t('dashboard.billing.paymentNote.closedVoid')
          : invoice.status === 'refunded'
            ? t('dashboard.billing.paymentNote.closedRefunded')
            : t('dashboard.billing.paymentNote.closedPartiallyRefunded')}
      </div>
    );
  }

  const hasLink = typeof invoice.paymentLink === 'string' && invoice.paymentLink.length > 0;
  const linkToShow = stage.kind === 'ok' ? stage.paymentUrl : invoice.paymentLink;

  async function regenerate() {
    setStage({ kind: 'busy' });
    try {
      const result = await callRetryToyyibPayBill({ tenantId, invoiceId: invoice.id });
      if (result.ok) {
        setStage({ kind: 'ok', paymentUrl: result.paymentUrl });
      } else {
        setStage({ kind: 'failed', message: result.message });
      }
    } catch (e) {
      const msg = (e as Error).message ?? 'Unknown error';
      setStage({ kind: 'failed', message: msg });
      logger.error('retryToyyibPayBill failed', { tenantId, invoiceId: invoice.id, error: msg });
    }
  }

  async function copyLink() {
    if (!linkToShow) return;
    try {
      await navigator.clipboard.writeText(linkToShow);
      setStage({ kind: 'copied' });
      setTimeout(() => setStage((s) => (s.kind === 'copied' ? { kind: 'idle' } : s)), 2000);
    } catch (e) {
      logger.warn('clipboard write failed', { error: String(e) });
    }
  }

  return (
    <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
      {hasLink || stage.kind === 'ok' ? (
        <>
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <a href={linkToShow} target="_blank" rel="noopener noreferrer" style={primaryCtaStyle}>
              {t('dashboard.billing.payment.payNow')}
            </a>
            <button type="button" onClick={copyLink} style={secondaryCtaStyle}>
              {stage.kind === 'copied'
                ? t('dashboard.billing.action.copied')
                : t('dashboard.billing.action.copyLink')}
            </button>
            <button
              type="button"
              onClick={regenerate}
              disabled={stage.kind === 'busy'}
              style={secondaryCtaStyle}
            >
              {stage.kind === 'busy'
                ? t('dashboard.billing.action.regenerating')
                : t('dashboard.billing.action.regenerate')}
            </button>
          </div>
          {stage.kind === 'ok' ? (
            <p style={successNoteStyle}>{t('dashboard.billing.payment.regenerated')}</p>
          ) : null}
          {stage.kind === 'failed' ? <p style={errorNoteStyle}>{stage.message}</p> : null}
        </>
      ) : (
        <>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t('dashboard.billing.payment.linkPending')}
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={regenerate}
              disabled={stage.kind === 'busy'}
              style={primaryCtaStyle}
            >
              {stage.kind === 'busy'
                ? t('dashboard.billing.action.generating')
                : t('dashboard.billing.action.generatePaymentLink')}
            </button>
          </div>
          {stage.kind === 'failed' ? <p style={errorNoteStyle}>{stage.message}</p> : null}
        </>
      )}
    </div>
  );
}

const paymentNoteStyle: React.CSSProperties = {
  marginTop: 'var(--space-3)',
  paddingTop: 'var(--space-3)',
  borderTop: '1px solid var(--color-border)',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const primaryCtaStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 14px',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  border: 'none',
  cursor: 'pointer',
};

const secondaryCtaStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 14px',
  borderRadius: 'var(--radius-pill)',
  background: 'transparent',
  color: 'var(--color-on-surface)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  border: '1px solid var(--color-border-strong)',
  cursor: 'pointer',
};

const successNoteStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-primary)',
};

const errorNoteStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-accent)',
};

/** Invoice-status label keys, hoisted so the switch below reads as a mapping. */
const K = {
  open: 'dashboard.billing.invoiceStatus.open',
  paid: 'dashboard.billing.invoiceStatus.paid',
  pastDue: 'dashboard.billing.invoiceStatus.pastDue',
  void: 'dashboard.billing.invoiceStatus.void',
  refunded: 'dashboard.billing.invoiceStatus.refunded',
  partialRefund: 'dashboard.billing.invoiceStatus.partialRefund',
} as const;

function InvoiceStatusPill({ status }: { readonly status: Invoice['status'] }) {
  // The word on an operator's own invoice. It read "Past due" in English to
  // every locale until 2026-08-25 — the one chip on this screen most likely to
  // need understanding in a hurry.
  const { t } = useI18n();
  const visual = (() => {
    switch (status) {
      case 'open':
        return { bg: 'rgba(245,158,11,0.10)', fg: '#b45309', label: t(K.open) };
      case 'paid':
        return { bg: 'rgba(13,122,79,0.10)', fg: 'var(--color-primary)', label: t(K.paid) };
      case 'past_due':
        return { bg: 'rgba(239,68,68,0.10)', fg: 'var(--color-accent)', label: t(K.pastDue) };
      case 'void':
        return { bg: 'var(--color-border)', fg: 'var(--color-on-surface-muted)', label: t(K.void) };
      case 'refunded':
        return {
          bg: 'var(--color-border)',
          fg: 'var(--color-on-surface-muted)',
          label: t(K.refunded),
        };
      case 'partially_refunded':
        return { bg: 'rgba(245,158,11,0.10)', fg: '#b45309', label: t(K.partialRefund) };
    }
  })();
  return (
    <span
      style={{
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        background: visual.bg,
        color: visual.fg,
        fontSize: 'var(--text-meta)',
        fontWeight: 500,
      }}
    >
      {visual.label}
    </span>
  );
}

/**
 * Super-admin only: force-runs the monthlyBilling job for the just-closed
 * period (or a specific YYYY-MM if needed for backfill). Idempotent — re-runs
 * yield "skipped" for already-generated invoices.
 */
/**
 * Super-admin only — rendered behind `isSuperAdmin` above.
 *
 * Its English tooltip is deliberate, matching the standing policy that
 * `superadmin-*` tooling is not translated. Flagged as untranslated copy once;
 * this note is here so it is not flagged again.
 */
function RunMonthlyBillingButton() {
  const [stage, setStage] = useState<
    | { kind: 'idle' }
    | { kind: 'running' }
    | { kind: 'done'; period: string; generated: number; skipped: number; errored: number }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function run() {
    setStage({ kind: 'running' });
    try {
      const result = await callRunMonthlyBillingNow();
      setStage({
        kind: 'done',
        period: result.period,
        generated: result.generatedCount,
        skipped: result.skippedCount,
        errored: result.erroredCount,
      });
      if (result.erroredCount > 0) {
        logger.warn('runMonthlyBillingNow: some tenants errored', { results: result.results });
      }
    } catch (e) {
      logger.error('runMonthlyBillingNow failed', { error: String(e) });
      setStage({ kind: 'error', message: String((e as Error).message ?? 'Run failed.') });
    }
  }

  const label = (() => {
    switch (stage.kind) {
      case 'idle':
        return 'Run monthly billing now';
      case 'running':
        return 'Running…';
      case 'done':
        return `${stage.period}: ${stage.generated} generated · ${stage.skipped} skipped${stage.errored > 0 ? ` · ${stage.errored} errored` : ''}`;
      case 'error':
        return 'Run failed — tap to retry';
    }
  })();

  return (
    <button
      type="button"
      onClick={() => void run()}
      disabled={stage.kind === 'running'}
      title="Force-run the monthly billing job for the just-closed period (normally fires on the 1st at 00:05 SGT)"
      style={{
        ...ghostLinkStyle,
        cursor: stage.kind === 'running' ? 'wait' : 'pointer',
        ...(stage.kind === 'done'
          ? { borderColor: 'rgba(13,122,79,0.32)', color: 'var(--color-primary)' }
          : {}),
        ...(stage.kind === 'error'
          ? { borderColor: 'rgba(239,68,68,0.32)', color: 'var(--color-accent)' }
          : {}),
        ...(stage.kind === 'running' ? { opacity: 0.6 } : {}),
      }}
    >
      {label}
    </button>
  );
}

// ── audit log card ────────────────────────────────────────────────────

function AuditLogCard({ events }: { readonly events: readonly SubscriptionEvent[] }) {
  const { t } = useI18n();
  return (
    <Surface as="section">
      <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
        {t('dashboard.billing.history.title')}
      </h2>
      <p
        style={{
          margin: 'var(--space-1) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t('dashboard.billing.history.subtitle')}
      </p>
      {events.length === 0 ? (
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            padding: 'var(--space-3) var(--space-4)',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            background: 'var(--tint-subtle)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {t('dashboard.billing.history.empty')}
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 'var(--space-3) 0 0 0',
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {events.slice(0, 20).map((ev) => (
            <li
              key={ev.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatDateTime(tsMs(ev.at))}
              </span>
              <span style={{ fontSize: 'var(--text-meta)' }}>{describeEvent(ev)}</span>
            </li>
          ))}
        </ul>
      )}
      {events.length > 20 ? (
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {events.length - 20 === 1
            ? t('dashboard.billing.history.overflowOne')
            : t('dashboard.billing.history.overflowOther', { n: events.length - 20 })}
        </p>
      ) : null}
    </Surface>
  );
}

// ── next-month forecast ───────────────────────────────────────────────

function NextMonthForecast({
  plan,
  activeOutletCount,
  pendingRemovalCount,
  now,
}: {
  readonly plan: NonNullable<ReturnType<typeof defaultPlans.find>>;
  readonly activeOutletCount: number;
  readonly pendingRemovalCount: number;
  readonly now: Date;
}) {
  const { t } = useI18n();
  // What the operator will be billed on the 1st of next month. Pending
  // removals "count" as active until month-end (operator pays for partial
  // month with no refund), but they DROP next cycle — so the forecast
  // subtracts them.
  //
  // We do NOT include proration here: any mid-month adds already triggered
  // their proration when addOutlet ran. Next month is a full-month bill.
  const forecastOutletCount = Math.max(0, activeOutletCount - pendingRemovalCount);
  const extras = Math.max(0, forecastOutletCount - plan.includedOutlets);
  const total = plan.basePrice + extras * plan.extraOutletPrice;

  // Plus: if today is mid-month and the operator just added an outlet,
  // show the proration that would apply.
  const partialMonthExtra = proration.prorateAt(plan.extraOutletPrice, now);

  return (
    <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
      <ForecastRow label={`${plan.name} base`} value={money.format(plan.basePrice)} />
      <ForecastRow
        label={
          extras === 0
            ? t('dashboard.billing.stat.extraOutlets')
            : `${extras} extra × ${money.format(plan.extraOutletPrice)}`
        }
        value={extras === 0 ? '—' : money.format(extras * plan.extraOutletPrice)}
      />
      <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: 0 }} />
      <ForecastRow
        label={t('dashboard.billing.stat.nextMonthTotal')}
        value={money.format(total)}
        bold
      />
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t('dashboard.billing.forecast.prorationRefLead')}{' '}
        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
          {money.format(partialMonthExtra)}
        </strong>{' '}
        {t('dashboard.billing.forecast.prorationRefTail')}
      </p>
    </div>
  );
}

function ForecastRow({
  label,
  value,
  bold,
}: {
  readonly label: string;
  readonly value: string;
  readonly bold?: boolean;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span
        style={{
          fontSize: bold ? 'var(--text-body)' : 'var(--text-meta)',
          color: bold ? 'var(--color-on-surface)' : 'var(--color-on-surface-muted)',
          fontWeight: bold ? 600 : 400,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: bold ? 'var(--text-body)' : 'var(--text-meta)',
          fontWeight: bold ? 600 : 400,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────

function buildPendingRemovals(
  events: readonly SubscriptionEvent[],
): { scheduledEventId: string; outletId: string; effectiveAtMs: number }[] {
  const cancelled = new Set<string>();
  const archived = new Set<string>();
  const scheduled = new Map<string, { outletId: string; effectiveAtMs: number }>();
  for (const ev of events) {
    if (ev.kind === 'outlet_removal_scheduled') {
      scheduled.set(ev.id, { outletId: ev.outletId, effectiveAtMs: tsMs(ev.effectiveAt) });
    } else if (ev.kind === 'outlet_removal_cancelled') {
      cancelled.add(ev.cancelledEventId);
    } else if (ev.kind === 'outlet_archived') {
      archived.add(ev.scheduledEventId);
    }
  }
  const out: { scheduledEventId: string; outletId: string; effectiveAtMs: number }[] = [];
  for (const [id, info] of scheduled.entries()) {
    if (cancelled.has(id) || archived.has(id)) continue;
    out.push({ scheduledEventId: id, outletId: info.outletId, effectiveAtMs: info.effectiveAtMs });
  }
  return out.sort((a, b) => a.effectiveAtMs - b.effectiveAtMs);
}

function describeEvent(ev: SubscriptionEvent): string {
  switch (ev.kind) {
    case 'outlet_added':
      return `Added outlet "${ev.outletName}" · prorated ${money.format(ev.prorationAmount)} for ${ev.daysInMonth - ev.day + 1} day${ev.daysInMonth - ev.day + 1 === 1 ? '' : 's'} of month`;
    case 'outlet_removal_scheduled':
      return `Scheduled closure of "${ev.outletName}" for ${formatDate(tsMs(ev.effectiveAt))}`;
    case 'outlet_removal_cancelled':
      return `Cancelled scheduled closure of "${ev.outletName}"`;
    case 'outlet_archived':
      return `Archived "${ev.outletName}" · outlet count now ${ev.outletCountAfter}${ev.by.role === 'system' ? ' (auto)' : ' (super-admin)'}`;
  }
}

function tsMs(ts: { toMillis?: () => number; seconds: number } | undefined): number {
  if (!ts) return 0;
  return typeof ts.toMillis === 'function' ? ts.toMillis() : ts.seconds * 1000;
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

function formatDateTime(ms: number): string {
  if (!ms) return '—';
  const d = new Date(ms);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function prettyPaymentMethod(m: SubscriptionState['paymentMethod']): string {
  switch (m) {
    case 'billplz_fpx':
      return 'Billplz FPX';
    case 'hitpay_duitnow_qr':
      return 'HitPay DuitNow QR';
    case 'manual_transfer':
      return 'Manual transfer';
  }
}

/** Plan-status label keys, hoisted for the same reason as the invoice table. */
const PK = {
  trial: 'dashboard.billing.planStatus.trial',
  active: 'dashboard.billing.planStatus.active',
  pastDue: 'dashboard.billing.planStatus.pastDue',
  suspended: 'dashboard.billing.planStatus.suspended',
  archived: 'dashboard.billing.planStatus.archived',
} as const;

function StatusBadge({ status }: { readonly status: SubscriptionState['status'] }) {
  // "Suspended" is the worst word to render in a language the operator does not
  // read: it is the state where their carwash has stopped taking bookings.
  const { t } = useI18n();
  const visual = (() => {
    switch (status) {
      case 'trial':
        return { bg: 'rgba(245,158,11,0.10)', fg: '#b45309', label: t(PK.trial) };
      case 'active':
        return { bg: 'rgba(13,122,79,0.10)', fg: 'var(--color-primary)', label: t(PK.active) };
      case 'past_due':
        return { bg: 'rgba(239,68,68,0.10)', fg: 'var(--color-accent)', label: t(PK.pastDue) };
      case 'suspended':
        return { bg: 'rgba(239,68,68,0.10)', fg: 'var(--color-accent)', label: t(PK.suspended) };
      case 'archived':
        return {
          bg: 'var(--color-border)',
          fg: 'var(--color-on-surface-muted)',
          label: t(PK.archived),
        };
    }
  })();
  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: 'var(--radius-pill)',
        background: visual.bg,
        color: visual.fg,
        fontSize: 'var(--text-meta)',
        fontWeight: 500,
      }}
    >
      {visual.label}
    </span>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
}) {
  return (
    <div>
      <dt style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          fontSize: 'var(--text-body)',
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </dd>
      {hint ? (
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Layout-matched loading state for the billing overview — mirrors the header +
 * two-column (main card stack · forecast aside) grid so the page doesn't jump
 * when the subscription + invoices resolve. Cards stagger in via `k-rise`;
 * shimmer via the shared `@kilat/ui` Skeleton. No visible text — `label` is
 * screen-reader-only status.
 */
function BillingSkeleton({ label }: { readonly label: string }) {
  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <span role="status" aria-live="polite" style={billingSrOnly}>
        {label}
      </span>
      <header
        aria-hidden
        style={{ marginBottom: 'var(--space-6)', display: 'grid', gap: 'var(--space-2)' }}
      >
        <Skeleton width={90} height={12} />
        <Skeleton width={220} height={30} />
      </header>
      <div aria-hidden className="layout-split-aside">
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {[0, 1, 2].map((i) => (
            <BillingCardSkeleton key={i} index={i} />
          ))}
        </div>
        <BillingCardSkeleton index={3} rows={6} />
      </div>
    </main>
  );
}

function BillingCardSkeleton({
  index,
  rows = 4,
}: {
  readonly index: number;
  readonly rows?: number;
}) {
  return (
    <section
      className="k-rise"
      style={
        {
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-5)',
          boxShadow: 'var(--shadow-1)',
          display: 'grid',
          gap: 'var(--space-3)',
          '--k-i': index,
        } as CSSProperties
      }
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Skeleton width={140} height={18} />
        <Skeleton width={64} height={22} radius="var(--radius-pill)" />
      </div>
      <Skeleton width="70%" height={12} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 'var(--space-3)',
        }}
      >
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} style={{ display: 'grid', gap: 6 }}>
            <Skeleton width="55%" height={10} />
            <Skeleton width="80%" height={16} />
          </div>
        ))}
      </div>
    </section>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div
        style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center', maxWidth: 380 }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {body}
        </p>
        {cta ? (
          <Link
            to={cta.to}
            style={{
              padding: '12px 18px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              textDecoration: 'none',
              fontWeight: 500,
              boxShadow: 'var(--shadow-2)',
            }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

// ── styles ────────────────────────────────────────────────────────────

const statGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--space-3) var(--space-4)',
  margin: 'var(--space-3) 0 0 0',
};

const miniGhostButtonStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  textDecoration: 'none',
  padding: '6px 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border-strong)',
  background: 'transparent',
  cursor: 'pointer',
};

// Sprint 98 — wallet-card primitives (parallels styles used in customers.tsx + superadmin-payouts.tsx).
const tinyGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '6px 10px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
};

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

// ── Sprint 91 — SST tax config card ────────────────────────────────────

/**
 * Operator-facing SST registration editor. Only the tenant owner sees
 * the "Save" button enabled (server enforces too). These details print in
 * the BILL TO block of the platform's subscription invoice. They do NOT
 * decide whether that invoice carries SST: the platform is the supplier
 * there, so SST follows `platformConfig/moneyPolicy.supplierTax` (fixed
 * 2026-10 — before that the operator's toggle added SST, backwards).
 */
// ── Wallet card (Sprint 98) ───────────────────────────────────────────

/**
 * Operator's wash-collection wallet — balance + recent ledger + bank
 * account editor + Request-payout CTA.
 *
 * Architecture per `docs/architecture/marketplace-payments.md`:
 *   - Customer pays via Kilat app → toyyibPay → Kilat's bank
 *   - Ledger entries (`tenants/{tid}/walletLedger`) accumulate operator
 *     balance; Kilat takes 0% margin (customer absorbs PSP fee at checkout)
 *   - Operator clicks "Request payout" → `runPayoutBatch` machinery
 *     (Sprint 97) → DuitNow CSV → operator's bank
 *
 * Three sub-sections per [no-dead-ends]:
 *   1. Balance + Request payout
 *   2. Bank account editor (dirty-flag tracking)
 *   3. Recent activity (collapsible; last 8 rows; full ledger drilldown TBD)
 *
 * Pre-Sprint-99 the only inflows are super-admin `adjustment` entries
 * (for demos / one-off corrections). Once Sprint 99 ships toyyibPay
 * webhook, `collected` entries land here automatically.
 */
function WalletCard({
  tenantId,
  payoutBankAccount,
  payoutBankLoadFailed,
  canEditPayoutBank,
  onPayoutBankAccountSaved,
}: {
  readonly tenantId: string;
  readonly payoutBankAccount: TenantPayoutBankAccount | null;
  readonly payoutBankLoadFailed: boolean;
  readonly canEditPayoutBank: boolean;
  readonly onPayoutBankAccountSaved: () => void;
}) {
  const { t } = useI18n();
  const brand = usePlatformBrand();
  const [entries, setEntries] = useState<TenantWalletLedgerEntry[] | null>(null);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setEntries(null);
    setLedgerError(null);
    const unsub = tenantWalletLedgerSnapshot(
      tenantId,
      (list) => {
        setEntries(list);
        setLedgerError(null);
      },
      (err) => {
        logger.error('walletLedger snapshot failed', { tenantId, error: String(err) });
        setLedgerError(
          err.message?.includes('permission')
            ? "You don't have permission to view this wallet."
            : 'Could not load your wallet. Refresh in a moment.',
        );
      },
    );
    return () => unsub();
  }, [tenantId, reloadTick]);

  const summary = useMemo(() => computeTenantWalletSummary(entries ?? []), [entries]);

  return (
    <Surface as="section">
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t('dashboard.billing.wallet.eyebrow')}
          </p>
          <h2
            style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h2)', fontWeight: 600 }}
          >
            {t('dashboard.billing.wallet.title')}
          </h2>
        </div>
      </header>

      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {t('dashboard.billing.wallet.intro', { brand: brand.name })}
      </p>

      {ledgerError ? (
        <div
          role="alert"
          style={{
            marginTop: 'var(--space-3)',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.20)',
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          <p style={{ margin: 0, fontSize: 'var(--text-meta)', color: '#991b1b' }}>{ledgerError}</p>
          <div>
            <button
              type="button"
              onClick={() => setReloadTick((n) => n + 1)}
              style={tinyGhostButtonStyle}
            >
              {t('dashboard.billing.common.retry')}
            </button>
          </div>
        </div>
      ) : entries === null ? (
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t('dashboard.billing.wallet.loading')}
        </p>
      ) : (
        <>
          <WalletBalanceRow
            tenantId={tenantId}
            summary={summary}
            payoutBankAccount={payoutBankAccount}
          />
          {payoutBankLoadFailed ? (
            <p
              role="alert"
              style={{
                margin: 'var(--space-3) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-accent)',
              }}
            >
              {t('dashboard.billing.payoutDest.loadFailed')}
            </p>
          ) : (
            <PayoutBankAccountEditor
              tenantId={tenantId}
              initial={payoutBankAccount}
              canEdit={canEditPayoutBank}
              onSaved={onPayoutBankAccountSaved}
            />
          )}
          <RecentWalletActivity entries={entries} />
        </>
      )}
    </Surface>
  );
}

function WalletBalanceRow({
  tenantId,
  summary,
  payoutBankAccount,
}: {
  readonly tenantId: string;
  readonly summary: ReturnType<typeof computeTenantWalletSummary>;
  readonly payoutBankAccount: TenantPayoutBankAccount | null;
}) {
  const { t } = useI18n();
  const [withdrawState, setWithdrawState] = useState<
    | { kind: 'idle' }
    | { kind: 'submitting' }
    | { kind: 'requested'; grossSen: number; payoutId: string }
    | { kind: 'refused'; message: string }
    | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  const hasDestination =
    !!payoutBankAccount &&
    (!!payoutBankAccount.duitNowId ||
      (!!payoutBankAccount.bankName &&
        !!payoutBankAccount.accountName &&
        !!payoutBankAccount.accountNumber));
  const hasBalance = summary.pendingExcludingWithdrawsSen > 0;

  async function request() {
    setWithdrawState({ kind: 'submitting' });
    try {
      const result = await callRequestTenantWithdraw({ tenantId });
      if (result.ok) {
        setWithdrawState({
          kind: 'requested',
          grossSen: result.grossSen,
          payoutId: result.payoutId,
        });
      } else {
        setWithdrawState({ kind: 'refused', message: result.message });
      }
    } catch (e) {
      logger.error('requestTenantWithdraw failed', { error: String(e) });
      const m = (e as Error).message ?? 'Could not request payout.';
      setWithdrawState({
        kind: 'failed',
        message: m.includes('permission-denied')
          ? 'Only the operator owner or manager can request a payout.'
          : m,
      });
    }
  }

  return (
    <div
      style={{
        marginTop: 'var(--space-4)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: 'rgba(13,122,79,0.06)',
        border: '1px solid rgba(13,122,79,0.18)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {t('dashboard.billing.wallet.availableToWithdraw')}
          </p>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-display)',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--color-primary)',
            }}
          >
            {money.format(summary.pendingExcludingWithdrawsSen)}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {summary.pendingCount === 1
              ? t('dashboard.billing.wallet.pendingSummaryOne', {
                  count: summary.pendingCount,
                  paidOut: money.format(summary.lifetimePaidOutSen),
                })
              : t('dashboard.billing.wallet.pendingSummaryOther', {
                  count: summary.pendingCount,
                  paidOut: money.format(summary.lifetimePaidOutSen),
                })}
          </p>
        </div>
      </div>

      {/* Request payout state machine — every branch explicit. */}
      {withdrawState.kind === 'requested' ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <p
            role="status"
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              fontWeight: 500,
              color: 'var(--color-primary)',
            }}
          >
            {t('dashboard.billing.wallet.payoutRequested', {
              amount: money.format(withdrawState.grossSen),
            })}
          </p>
          <div>
            <button
              type="button"
              onClick={() => setWithdrawState({ kind: 'idle' })}
              style={tinyGhostButtonStyle}
            >
              {t('dashboard.billing.wallet.dismiss')}
            </button>
          </div>
        </div>
      ) : withdrawState.kind === 'refused' || withdrawState.kind === 'failed' ? (
        <div
          role="alert"
          style={{
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.22)',
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          <p style={{ margin: 0, fontSize: 'var(--text-meta)', color: '#991b1b' }}>
            {withdrawState.message}
          </p>
          <div>
            <button
              type="button"
              onClick={() => setWithdrawState({ kind: 'idle' })}
              style={tinyGhostButtonStyle}
            >
              {t('dashboard.billing.wallet.dismiss')}
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}
        >
          <button
            type="button"
            onClick={() => void request()}
            disabled={withdrawState.kind === 'submitting' || !hasDestination || !hasBalance}
            style={{
              ...primaryButtonStyle,
              opacity:
                withdrawState.kind === 'submitting' || !hasDestination || !hasBalance ? 0.5 : 1,
              cursor:
                withdrawState.kind === 'submitting' || !hasDestination || !hasBalance
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            {withdrawState.kind === 'submitting'
              ? t('dashboard.billing.action.requesting')
              : t('dashboard.billing.action.requestPayout')}
          </button>
          {!hasDestination ? (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {t('dashboard.billing.wallet.needDestination')}
            </span>
          ) : !hasBalance ? (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {t('dashboard.billing.wallet.nothingToWithdraw')}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

function PayoutBankAccountEditor({
  tenantId,
  initial,
  canEdit,
  onSaved,
}: {
  readonly tenantId: string;
  readonly initial: TenantPayoutBankAccount | null;
  /** Owner only — the callable refuses everyone else, so do not offer it. */
  readonly canEdit: boolean;
  readonly onSaved: () => void;
}) {
  const { t } = useI18n();
  const [bankName, setBankName] = useState(initial?.bankName ?? '');
  const [accountName, setAccountName] = useState(initial?.accountName ?? '');
  const [accountNumber, setAccountNumber] = useState(initial?.accountNumber ?? '');
  const [duitNowId, setDuitNowId] = useState(initial?.duitNowId ?? '');
  const [thresholdRm, setThresholdRm] = useState(
    initial ? ((initial.thresholdSen ?? 10000) / 100).toFixed(2) : '100.00',
  );
  const [autoWithdraw, setAutoWithdraw] = useState(initial?.autoWithdrawEnabled ?? false);
  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  // Re-hydrate when upstream value lands (initial load completes after the
  // editor mounts) — but ONLY when not actively editing.
  useEffect(() => {
    if (stage.kind === 'saving') return;
    setBankName(initial?.bankName ?? '');
    setAccountName(initial?.accountName ?? '');
    setAccountNumber(initial?.accountNumber ?? '');
    setDuitNowId(initial?.duitNowId ?? '');
    setThresholdRm(initial ? ((initial.thresholdSen ?? 10000) / 100).toFixed(2) : '100.00');
    setAutoWithdraw(initial?.autoWithdrawEnabled ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const thresholdSenParsed = (() => {
    const n = Number.parseFloat(thresholdRm.trim());
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
  })();
  const thresholdValid = thresholdSenParsed !== null;

  // Validity: either a DuitNow id, OR a full bank triple. Both blank
  // is also valid (clears the destination — operator might pause auto).
  const bankTripleFilled =
    bankName.trim().length > 0 && accountName.trim().length > 0 && accountNumber.trim().length > 0;
  const bankTriplePartial =
    !bankTripleFilled &&
    (bankName.trim().length > 0 ||
      accountName.trim().length > 0 ||
      accountNumber.trim().length > 0);
  const destinationValid = !bankTriplePartial; // duitNow alone OR full triple OR all-blank all OK
  const canSave = canEdit && destinationValid && thresholdValid && stage.kind !== 'saving';

  async function save() {
    if (!canSave) return;
    setStage({ kind: 'saving' });
    try {
      // Through the owner-only callable: the private document denies every
      // client write, and the server stamps updatedBy/updatedAt + history.
      await callSetPayoutBankAccount({
        tenantId,
        ...(bankName.trim() ? { bankName: bankName.trim() } : {}),
        ...(accountName.trim() ? { accountName: accountName.trim() } : {}),
        ...(accountNumber.trim() ? { accountNumber: accountNumber.trim() } : {}),
        ...(duitNowId.trim() ? { duitNowId: duitNowId.trim() } : {}),
        thresholdSen: thresholdSenParsed ?? 10000,
        autoWithdrawEnabled: autoWithdraw,
      });
      logger.info('payoutBankAccount saved', { tenantId });
      setStage({ kind: 'saved' });
      onSaved();
      setTimeout(() => setStage({ kind: 'idle' }), 1200);
    } catch (e) {
      logger.error('payoutBankAccount save failed', { error: String(e) });
      const m = (e as Error).message ?? 'Could not save.';
      setStage({
        kind: 'failed',
        message: m.includes('permission') ? t('dashboard.billing.payoutDest.ownerOnly') : m,
      });
    }
  }

  return (
    <details
      style={{
        marginTop: 'var(--space-3)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
      }}
    >
      <summary style={{ cursor: 'pointer', fontSize: 'var(--text-body)', fontWeight: 500 }}>
        {t('dashboard.billing.payoutDest.title')}
        {initial ? (
          <span
            style={{
              marginLeft: 8,
              fontWeight: 400,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {initial.duitNowId
              ? t('dashboard.billing.payoutDest.currentDuitNow', { id: initial.duitNowId })
              : initial.accountNumber
                ? t('dashboard.billing.payoutDest.currentBank', {
                    bank: initial.bankName ?? t('dashboard.billing.payoutDest.bankFallback'),
                    account: initial.accountNumber,
                  })
                : t('dashboard.billing.payoutDest.currentIncomplete')}
          </span>
        ) : (
          <span
            style={{
              marginLeft: 8,
              fontWeight: 400,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t('dashboard.billing.payoutDest.notSet')}
          </span>
        )}
      </summary>

      {canEdit ? null : (
        <p
          role="note"
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('dashboard.billing.payoutDest.ownerOnly')}
        </p>
      )}

      {/* A disabled fieldset makes every input read-only for a non-owner in
          one place, rather than threading `disabled` through each field. */}
      <fieldset
        disabled={!canEdit}
        style={{
          marginTop: 'var(--space-3)',
          display: 'grid',
          gap: 'var(--space-3)',
          border: 0,
          padding: 0,
          minWidth: 0,
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('dashboard.billing.payoutDest.explainer')}
        </p>

        <label style={{ display: 'grid', gap: 4 }}>
          <span style={fieldLabel}>{t('dashboard.billing.payoutDest.duitNowLabel')}</span>
          <input
            type="text"
            value={duitNowId}
            onChange={(e) => setDuitNowId(e.target.value)}
            placeholder={t('dashboard.billing.payoutField.accountPlaceholder')}
            maxLength={40}
            style={inputStyle}
            autoComplete="off"
          />
        </label>

        <div
          style={{
            paddingTop: 'var(--space-2)',
            borderTop: '1px dashed var(--color-border-strong)',
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t('dashboard.billing.payoutDest.orBank')}
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
              gap: 'var(--space-2)',
            }}
          >
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={fieldLabel}>{t('dashboard.billing.payoutDest.bank')}</span>
              <input
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder={t('dashboard.billing.payoutField.bankPlaceholder')}
                maxLength={80}
                style={inputStyle}
                autoComplete="off"
              />
            </label>
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={fieldLabel}>{t('dashboard.billing.payoutDest.accountName')}</span>
              <input
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder={t('dashboard.billing.payoutField.holderPlaceholder')}
                maxLength={100}
                style={inputStyle}
                autoComplete="off"
              />
            </label>
          </div>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={fieldLabel}>{t('dashboard.billing.payoutDest.accountNumber')}</span>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder={t('dashboard.billing.payoutField.numbersOnly')}
              maxLength={40}
              style={inputStyle}
              autoComplete="off"
            />
          </label>
          {bankTriplePartial ? (
            <p
              role="alert"
              style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}
            >
              {t('dashboard.billing.payoutDest.partialTriple')}
            </p>
          ) : null}
        </div>

        <label style={{ display: 'grid', gap: 4 }}>
          <span style={fieldLabel}>{t('dashboard.billing.payoutDest.thresholdLabel')}</span>
          <input
            type="text"
            inputMode="decimal"
            value={thresholdRm}
            onChange={(e) => setThresholdRm(e.target.value)}
            placeholder={t('dashboard.billing.payoutField.amountPlaceholder')}
            style={inputStyle}
            autoComplete="off"
          />
          {!thresholdValid ? (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}>
              {t('dashboard.billing.payoutDest.thresholdInvalid')}
            </span>
          ) : (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {t('dashboard.billing.payoutDest.thresholdHint')}
            </span>
          )}
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <input
            type="checkbox"
            checked={autoWithdraw}
            onChange={(e) => setAutoWithdraw(e.target.checked)}
          />
          <span style={{ fontSize: 'var(--text-meta)' }}>
            {t('dashboard.billing.payoutDest.autoWithdrawToggle')}
          </span>
        </label>

        {stage.kind === 'failed' ? (
          <p
            role="alert"
            style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
          >
            {stage.message}
          </p>
        ) : null}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            justifyContent: 'flex-end',
          }}
        >
          {stage.kind === 'saved' ? (
            <span
              style={{
                color: 'var(--color-primary)',
                fontSize: 'var(--text-meta)',
                fontWeight: 600,
              }}
            >
              {t('dashboard.billing.payoutDest.saved')}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => void save()}
            disabled={!canSave}
            style={{
              ...primaryButtonStyle,
              opacity: canSave ? 1 : 0.5,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {stage.kind === 'saving'
              ? t('dashboard.billing.action.saving')
              : t('dashboard.billing.action.saveDestination')}
          </button>
        </div>
      </fieldset>
    </details>
  );
}

function RecentWalletActivity({
  entries,
}: {
  readonly entries: readonly TenantWalletLedgerEntry[];
}) {
  const { t } = useI18n();
  const recent = entries.slice(0, 8);
  if (recent.length === 0) {
    return (
      <p
        style={{
          margin: 'var(--space-3) 0 0 0',
          padding: 'var(--space-3)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--tint-subtle)',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t('dashboard.billing.walletActivity.empty')}
      </p>
    );
  }
  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <h3 style={{ margin: '0 0 var(--space-2) 0', fontSize: 'var(--text-body)', fontWeight: 600 }}>
        {t('dashboard.billing.walletActivity.title')}
      </h3>
      <ul
        style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-1)' }}
      >
        {recent.map((e) => (
          <li
            key={e.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1fr) auto',
              gap: 'var(--space-3)',
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--tint-subtle)',
              alignItems: 'baseline',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <span style={{ fontSize: 'var(--text-meta)', fontWeight: 500 }}>
                {walletKindLabel(e.kind)}
              </span>
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {formatWalletDate(e.accruedAt)}
                {e.payoutId ? t('dashboard.billing.action.paidOut') : ''}
              </span>
            </div>
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 600,
                color: e.amount < 0 ? 'var(--color-on-surface-muted)' : 'var(--color-on-surface)',
              }}
            >
              {e.amount > 0 ? '+' : ''}
              {money.format(e.amount)}
            </span>
          </li>
        ))}
      </ul>
      {entries.length > recent.length ? (
        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t('dashboard.billing.walletActivity.showing', {
            shown: recent.length,
            total: entries.length,
          })}
        </p>
      ) : null}
    </div>
  );
}

function walletKindLabel(kind: TenantWalletLedgerEntry['kind']): string {
  switch (kind) {
    case 'collected':
      return 'Customer payment';
    case 'psp_fee':
      return 'Payment processing fee';
    case 'refund':
      return 'Refund';
    case 'withdraw':
      return 'Payout';
    case 'adjustment':
      return 'Adjustment';
  }
}

function formatWalletDate(ts: { toMillis?: () => number; seconds?: number } | undefined): string {
  if (!ts || typeof ts !== 'object') return '—';
  const obj = ts as { toMillis?: () => number; seconds?: number };
  const ms =
    typeof obj.toMillis === 'function'
      ? obj.toMillis()
      : typeof obj.seconds === 'number'
        ? obj.seconds * 1000
        : 0;
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * SST details, loaded from `tenants/{tid}/private/tax`.
 *
 * This used to read `profile.tax`, which came free with the tenant document the
 * page already had. That document is world-readable — `firestore.rules` grants
 * `allow read: if true` so the unauthenticated customer PWA can boot from a
 * hostname — and this block holds the owner's NRIC and home address whenever
 * the business is a sole proprietorship. Moved 2026-08-29; the one extra read
 * buys the operator their privacy back.
 */
function TaxConfigCard({ tenantId }: { readonly tenantId: string }) {
  const { t } = useI18n();
  const [saved, setSaved] = useState<TenantTaxDoc | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [registered, setRegistered] = useState(false);
  const [sstNumber, setSstNumber] = useState('');
  const [rate, setRate] = useState(String(0.08 * 100));
  const [companyName, setCompanyName] = useState('');
  const [companyRegistration, setCompanyRegistration] = useState('');
  const [registeredAddress, setRegisteredAddress] = useState('');
  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // `reloadTick` re-runs this after a save so the form's baseline follows what
  // the server actually stored, exactly as re-reading `profile.tax` used to.
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // No document is the NORMAL case — a tenant that never registered for
        // SST. That is a ready empty form, not an error.
        const doc = await tenantTaxRepoFor(tenantId).get(TENANT_TAX_DOC_ID);
        if (cancelled) return;
        setSaved(doc ?? null);
        setRegistered(doc?.registered === true);
        setSstNumber(doc?.sstNumber ?? '');
        setRate(String((doc?.rate ?? 0.08) * 100));
        setCompanyName(doc?.companyName ?? '');
        setCompanyRegistration(doc?.companyRegistration ?? '');
        setRegisteredAddress(doc?.registeredAddress ?? '');
        setStage({ kind: 'idle' });
        setLoadState('ready');
      } catch (e) {
        logger.error('tenant tax config load failed', { tenantId, error: String(e) });
        if (!cancelled) setLoadState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, reloadTick]);

  const dirty =
    registered !== (saved?.registered === true) ||
    sstNumber !== (saved?.sstNumber ?? '') ||
    String((saved?.rate ?? 0.08) * 100) !== rate ||
    companyName !== (saved?.companyName ?? '') ||
    companyRegistration !== (saved?.companyRegistration ?? '') ||
    registeredAddress !== (saved?.registeredAddress ?? '');

  async function handleSave() {
    if (registered && !sstNumber.trim()) {
      setStage({ kind: 'error', message: t('dashboard.billing.tax.sstRequired') });
      return;
    }
    const rateDecimal = Number(rate) / 100;
    if (registered && (!Number.isFinite(rateDecimal) || rateDecimal < 0 || rateDecimal > 1)) {
      setStage({ kind: 'error', message: t('dashboard.billing.tax.rateRange') });
      return;
    }
    setStage({ kind: 'saving' });
    try {
      await callSetTenantTaxConfig({
        tenantId,
        registered,
        ...(registered
          ? {
              sstNumber: sstNumber.trim(),
              rate: rateDecimal,
              ...(companyName.trim() ? { companyName: companyName.trim() } : {}),
              ...(companyRegistration.trim()
                ? { companyRegistration: companyRegistration.trim() }
                : {}),
              ...(registeredAddress.trim() ? { registeredAddress: registeredAddress.trim() } : {}),
            }
          : {}),
      });
      setStage({ kind: 'saved' });
      // Re-read what the server stored, so the form's baseline is the truth
      // (the callable can normalise or clear fields) rather than what was typed.
      setReloadTick((n) => n + 1);
    } catch (e) {
      logger.error('setTenantTaxConfig failed', { tenantId, error: String(e) });
      // The raw server message is for the log above, not for the operator.
      setStage({ kind: 'error', message: t('dashboard.billing.tax.saveFailed') });
    }
  }

  return (
    <Surface as="section">
      <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
        {t('dashboard.billing.tax.title')}
      </h2>
      <p
        style={{
          margin: 'var(--space-1) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {t('dashboard.billing.tax.explainer')}
      </p>

      {loadState === 'loading' ? (
        <div style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-2)' }}>
          <Skeleton height="3rem" />
          <Skeleton height="3rem" width="60%" />
          <span style={billingSrOnly}>{t('dashboard.billing.tax.loading')}</span>
        </div>
      ) : loadState === 'error' ? (
        // Never an empty form here: saving one would overwrite real tax details
        // with blanks. Say what happened, and offer the one thing that helps.
        <div
          role="alert"
          style={{
            marginTop: 'var(--space-4)',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-danger-bg)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            fontSize: 'var(--text-meta)',
          }}
        >
          <span>{t('dashboard.billing.tax.loadFailed')}</span>
          <button
            type="button"
            onClick={() => {
              setLoadState('loading');
              setReloadTick((n) => n + 1);
            }}
            style={primaryButtonStyle}
          >
            {t('common.tryAgain')}
          </button>
        </div>
      ) : (
        <>
          <label
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              alignItems: 'center',
              marginTop: 'var(--space-4)',
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: 'var(--tint-subtle)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={registered}
              onChange={(e) => setRegistered(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontSize: 'var(--text-meta)' }}>
              <strong style={{ fontWeight: 600 }}>
                {t('dashboard.billing.tax.registeredLead')}
              </strong>{' '}
              {t('dashboard.billing.tax.registeredBody')}
            </span>
          </label>

          {registered ? (
            <div
              style={{
                marginTop: 'var(--space-4)',
                display: 'grid',
                gap: 'var(--space-3)',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
              }}
            >
              <TaxField
                label={t('dashboard.billing.taxField.sstNumber')}
                value={sstNumber}
                onChange={setSstNumber}
                placeholder={t('dashboard.billing.taxField.sstPlaceholder')}
                required
              />
              <TaxField
                label={t('dashboard.billing.taxField.taxRate')}
                value={rate}
                onChange={setRate}
                placeholder={t('dashboard.billing.taxField.taxRatePlaceholder')}
                inputMode="decimal"
              />
              <TaxField
                label={t('dashboard.billing.taxField.legalName')}
                value={companyName}
                onChange={setCompanyName}
                placeholder={t('dashboard.billing.taxField.legalNamePlaceholder')}
              />
              <TaxField
                label={t('dashboard.billing.taxField.registrationNo')}
                value={companyRegistration}
                onChange={setCompanyRegistration}
                placeholder={t('dashboard.billing.taxField.registrationPlaceholder')}
              />
              <label style={{ display: 'grid', gap: 'var(--space-2)', gridColumn: '1 / -1' }}>
                <span
                  style={{
                    fontSize: 'var(--text-meta)',
                    color: 'var(--color-on-surface-muted)',
                    fontWeight: 500,
                  }}
                >
                  {t('dashboard.billing.tax.registeredAddress')}
                </span>
                <textarea
                  value={registeredAddress}
                  onChange={(e) => setRegisteredAddress(e.target.value)}
                  rows={3}
                  placeholder={t('dashboard.billing.taxField.addressPlaceholder')}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--color-border-strong)',
                    fontSize: 'var(--text-body)',
                    fontFamily: 'inherit',
                    background: 'var(--color-surface)',
                    color: 'var(--color-on-surface)',
                    resize: 'vertical',
                  }}
                />
              </label>
            </div>
          ) : null}

          {stage.kind === 'error' ? (
            <p
              role="alert"
              style={{
                margin: 'var(--space-3) 0 0 0',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-accent)',
                background: 'rgba(239,68,68,0.06)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {stage.message}
            </p>
          ) : null}
          {stage.kind === 'saved' && !dirty ? (
            <p
              role="status"
              style={{
                margin: 'var(--space-3) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-primary)',
              }}
            >
              {t('dashboard.billing.tax.savedNote')}
            </p>
          ) : null}

          <div
            style={{
              marginTop: 'var(--space-4)',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!dirty || stage.kind === 'saving'}
              style={{
                background: 'var(--color-primary)',
                color: 'var(--color-primary-fg)',
                padding: '0.5em 1em',
                minHeight: 'var(--touch-target)',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                fontSize: 'var(--text-meta)',
                fontWeight: 500,
                cursor: !dirty || stage.kind === 'saving' ? 'not-allowed' : 'pointer',
                opacity: !dirty || stage.kind === 'saving' ? 0.55 : 1,
                boxShadow: 'var(--shadow-1)',
              }}
            >
              {stage.kind === 'saving'
                ? t('dashboard.billing.action.saving')
                : dirty
                  ? t('dashboard.billing.taxField.save')
                  : t('dashboard.billing.taxField.saved')}
            </button>
          </div>
        </>
      )}
    </Surface>
  );
}

function TaxField({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  required,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (s: string) => void;
  readonly placeholder?: string;
  readonly inputMode?: 'text' | 'numeric' | 'decimal' | 'tel';
  readonly required?: boolean;
}) {
  return (
    <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          fontWeight: 500,
        }}
      >
        {label}
        {required ? <span style={{ color: 'var(--color-accent)' }}> *</span> : null}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        style={{
          padding: '12px 14px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--color-border-strong)',
          fontSize: 'var(--text-body)',
          fontFamily: 'inherit',
          background: 'var(--color-surface)',
          color: 'var(--color-on-surface)',
          width: '100%',
        }}
      />
    </label>
  );
}

// ── Sprint 91 — Invoice PDF download button ────────────────────────────

function InvoicePdfButton({
  tenantId,
  invoiceId,
}: {
  readonly tenantId: string;
  readonly invoiceId: string;
}) {
  const [stage, setStage] = useState<
    | { kind: 'idle' }
    | { kind: 'generating' }
    | { kind: 'ready'; downloadURL: string }
    | { kind: 'failed'; message: string }
  >({ kind: 'idle' });
  const { t } = useI18n();

  async function generate() {
    setStage({ kind: 'generating' });
    try {
      const result = await callGenerateInvoicePdf({ tenantId, invoiceId });
      setStage({ kind: 'ready', downloadURL: result.downloadURL });
      // Auto-open in new tab so the operator gets the PDF immediately.
      window.open(result.downloadURL, '_blank', 'noopener,noreferrer');
    } catch (e) {
      logger.error('generateInvoicePdf failed', { invoiceId, error: String(e) });
      setStage({
        kind: 'failed',
        message: String((e as Error).message ?? 'Could not generate PDF.'),
      });
    }
  }

  if (stage.kind === 'ready') {
    return (
      <a
        href={stage.downloadURL}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          ...miniGhostButtonStyle,
          color: 'var(--color-primary)',
          borderColor: 'rgba(13,122,79,0.32)',
        }}
      >
        {t('dashboard.billing.pdf.open')}
      </a>
    );
  }
  if (stage.kind === 'failed') {
    return (
      <div style={{ display: 'inline-flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <span role="alert" style={{ fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}>
          {stage.message}
        </span>
        <button type="button" onClick={() => void generate()} style={miniGhostButtonStyle}>
          {t('dashboard.billing.common.retry')}
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => void generate()}
      disabled={stage.kind === 'generating'}
      style={{
        ...miniGhostButtonStyle,
        opacity: stage.kind === 'generating' ? 0.6 : 1,
        cursor: stage.kind === 'generating' ? 'wait' : 'pointer',
      }}
    >
      {stage.kind === 'generating'
        ? t('dashboard.billing.action.generatingPdf')
        : t('dashboard.billing.action.downloadPdf')}
    </button>
  );
}
